/**
 * Subscriptions / payments page. Renders current subscription (from Firestore) and plan cards.
 * Purchase flow: user taps Subscribe → requestFlutterPurchase (WebView→Flutter) → native IAP
 * → Flutter calls validatePurchase → subscription stored; we show success and refresh. See docs/PAYMENTS.md.
 * Users must be logged in to subscribe; otherwise a message is shown and subscribe is disabled.
 *
 * Coupon codes:
 *  - free: redeem without IAP; shows a "Redeem for Free" button on the annual plan.
 *  - percent: applied after IAP completes; extends the subscription duration server-side.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SubscriptionSuccessPopup from '../components/SubscriptionSuccessPopup';
import CurrentSubscriptionSection, {
  type LastPurchasedSubscription,
} from '../components/CurrentSubscriptionSection';
import { requestFlutterPurchase } from '../components/useFlutterPurchase';
import CouponInput, { type CouponResult } from '../components/CouponInput';
import { FIREBASE_FUNCTIONS, callCloudFunction } from '../utils/firebaseFunctions';
import { trackPurchase } from '../utils/analytics';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { firebaseApp } from '../utils/firebaseConfig';
import cheeringKidsImg from '../assets/webp/cheering-kids.webp';
import yamen1Img from '../assets/webp/yamen1.webp';
import BottomNav from '../components/BottomNav';

const db = getFirestore(firebaseApp);
const LOGGED_IN_USER_KEY = 'loggedInUser';

function isRealLoggedInUser(userId: string | null): boolean {
  if (!userId || userId.trim() === '') return false;
  const lower = userId.toLowerCase();
  return lower !== 'guest' && !lower.startsWith('guest-');
}

interface Plan {
  id: string;
  base_plan?: string;
  title: string;
  price: string;
  description: string;
  recommended?: boolean;
  discount?: string;
}

const MAIN_PLANS: Plan[] = [
  {
    id: 'month1',
    title: 'חודשי',
    price: '55$',
    description: 'גישה מלאה לכל התכנים והפרשות',
  },
  {
    id: '3months1',
    title: '3 חודשים',
    price: '149$',
    description: 'גישה מלאה לכל התכנים והפרשות',
    recommended: true,
    discount: '10% הנחה',
  },
  {
    id: 'yearly',
    title: 'שנתי',
    price: '480$',
    description: 'גישה מלאה לכל התכנים והפרשות',
    discount: '22% הנחה',
  },
];

const YEMEN_PLANS: { id: string; title: string; price: string }[] = [
  { id: 'yamen1', title: 'חודשי', price: '25$' },
  { id: 'yamen3months1', title: '3 חודשים', price: '60$' },
  { id: 'yamen1yearly1', title: 'שנתי', price: '180$' },
];

const FREE_FEATURES = [
  '2 פסוקים ראשונים בלבד',
  'ללא ניתוח והשוואה ב-AI',
  'ללא אנליטיקה מתקדמת',
  'ללא מעקב התקדמות',
  'ללא מעקב הורים',
  'כולל פרסומות',
];

const PREMIUM_FEATURES = [
  'כל הפרשות ללא הגבלה',
  'ניתוח והשוואה מידי ב-AI',
  'אנליטיקה מתקדמת',
  'מעקב התקדמות',
  'הוספה של עד 2 הורים למנוי',
  'ללא פרסומות',
];

// ─── Sub-components ────────────────────────────────────────────────────────

function applyDiscountToPrice(priceStr: string, pct: number): string {
  const match = priceStr.match(/(\d+(\.\d+)?)/);
  if (!match) return priceStr;
  const num = parseFloat(match[1]);
  const discounted = Math.round(num * (1 - pct / 100));
  const symbol = priceStr.replace(/[\d.]/g, '').trim();
  return `${discounted}${symbol}`;
}

interface PlanCardProps {
  plan: Plan;
  isLoading: boolean;
  disabled: boolean;
  isFreeRedeem: boolean;
  isRedeemLoading: boolean;
  onSubscribe: (id: string) => void;
  onRedeemFree: (id: string) => void;
  freeCouponDays?: number;
  discountPercent?: number;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isLoading,
  disabled,
  isFreeRedeem,
  isRedeemLoading,
  onSubscribe,
  onRedeemFree,
  freeCouponDays,
  discountPercent,
}) => {
  const discountedPrice = discountPercent
    ? applyDiscountToPrice(plan.price, discountPercent)
    : null;

  const isDisabled = isFreeRedeem ? isRedeemLoading : disabled || isLoading;
  const handleClick = () => (isFreeRedeem ? onRedeemFree(plan.id) : onSubscribe(plan.id));

  return (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={isDisabled ? undefined : handleClick}
      onKeyDown={(e) => !isDisabled && (e.key === 'Enter' || e.key === ' ') && handleClick()}
      style={{
        position: 'relative',
        border: '3px solid #F2CC0D',
        borderRadius: 16,
        padding: plan.recommended ? '28px 16px 16px' : '16px',
        background: '#fff',
        marginBottom: 12,
        boxShadow: plan.recommended
          ? '0 2px 12px rgba(245,158,11,0.15)'
          : '0 1px 4px rgba(0,0,0,0.05)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        userSelect: 'none',
      }}
    >
      {plan.recommended && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#F2CC0D',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            padding: '4px 14px',
            borderRadius: '0 0 10px 10px',
            whiteSpace: 'nowrap',
          }}
        >
          המומלץ ביותר
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          direction: 'rtl',
          marginBottom: 4,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#6B7280' }}>{plan.title}</h3>
        <div style={{ textAlign: 'left' }}>
          {discountedPrice && (
            <span
              style={{
                display: 'block',
                fontSize: 13,
                color: '#9CA3AF',
                textDecoration: 'line-through',
                textAlign: 'center',
                marginBottom: 2,
              }}
            >
              {plan.price}
            </span>
          )}
          <span
            style={{
              display: 'block',
              background: isFreeRedeem
                ? '#059669'
                : discountedPrice
                  ? '#059669'
                  : isLoading || isRedeemLoading
                    ? '#9CA3AF'
                    : '#0050CB',
              color: '#fff',
              fontSize: 18,
              width: 80,
              fontWeight: 800,
              padding: '6px 14px',
              borderRadius: 10,
              textAlign: 'center',
            }}
          >
            {isLoading || isRedeemLoading ? '...' : (discountedPrice ?? plan.price)}
          </span>
          {discountedPrice && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: '#059669' }}>
              {discountPercent}% הנחה
            </span>
          )}
          {!discountedPrice && plan.discount && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: '#EF4444' }}>
              {plan.discount}
            </span>
          )}
        </div>
      </div>

      <p
        style={{
          margin: '0 0 0',
          fontSize: 13,
          color: '#6B7280',
          textAlign: 'right',
          direction: 'rtl',
        }}
      >
        {plan.description}
      </p>
    </div>
  );
};

const ComparisonTable: React.FC = () => (
  <div
    style={{
      borderRadius: 16,
      background: '#111827',
      border: '2px solid #F2CC0D',
      padding: '20px 16px 16px',
      marginBottom: 20,
      direction: 'rtl',
    }}
  >
    {/* Column headers */}
    <div style={{ display: 'flex', marginBottom: 14 }}>
      <div style={{ flex: 1, paddingRight: 8, borderRight: '1px solid #374151' }}>
        <span
          style={{
            fontSize: 26,
            fontWeight: 900,
            color: '#F2CC0D',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          ✪ פרימיום
        </span>
      </div>
      <div style={{ flex: 1, paddingLeft: 8, textAlign: 'center' }}>
        <span style={{ fontSize: 26, fontWeight: 900, color: '#fff' }}>חינם</span>
      </div>
    </div>

    {/* Feature rows */}
    <div style={{ display: 'flex', gap: 0 }}>
      <div
        style={{
          flex: 1,
          paddingRight: 8,
          borderRight: '1px solid #374151',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {PREMIUM_FEATURES.map((f) => (
          <span key={f} style={{ fontSize: 12, color: '#D1D5DB', lineHeight: 1.5 }}>
            {f}
          </span>
        ))}
      </div>
      <div
        style={{
          flex: 1,
          paddingLeft: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          textAlign: 'right',
          marginRight: 30,
        }}
      >
        {FREE_FEATURES.map((f) => (
          <span key={f} style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
            {f}
          </span>
        ))}
      </div>
    </div>

    {/* Footer */}
    <p
      style={{
        marginTop: 16,
        marginBottom: 0,
        fontSize: 12,
        color: '#6B7280',
        textAlign: 'center',
        lineHeight: 1.6,
      }}
    >
      חיוב מאובטח • ניתן לבטל בכל עת • כפוף לתקנון
    </p>
  </div>
);

//@ts-ignore
const YemenSection: React.FC<{
  isLoggedIn: boolean;
  loadingId: string | null;
  onSubscribe: (id: string) => void;
}> = ({ isLoggedIn, loadingId, onSubscribe }) => {
  return (
    <div style={{ marginBottom: 20, borderRadius: 16, overflow: 'visible' }}>
      {/* Yellow header */}
      <div
        style={{
          position: 'relative',
          background: '#FCD34D',
          borderRadius: '16px 16px 0 0',
          height: 120,
          direction: 'rtl',
          overflow: 'hidden',
        }}
      >
        {/* Text — vertically centered, left side */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '58%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '0 0 0 16px',
            textAlign: 'left',
          }}
        >
          <p style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#111827' }}>
            מיוחד ובלעדי!
          </p>
          <p
            style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#374151', lineHeight: 1.4 }}
          >
            מנוי לעליה שישית בלבד
            <br />
            בנוסח תימן
          </p>
        </div>
        {/* Character fills the right side, bottom-aligned */}
        <img
          src={yamen1Img}
          alt=""
          style={{
            position: 'absolute',
            bottom: 0,
            right: 8,
            height: 110,
            width: 'auto',
          }}
        />
      </div>

      {/* Plan rows */}
      <div
        style={{ background: '#FCD34D', borderRadius: '0 0 16px 16px', padding: '8px 12px 16px' }}
      >
        {YEMEN_PLANS.map((plan) => {
          const isLoading = loadingId === plan.id;
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onSubscribe(plan.id)}
              disabled={!isLoggedIn || isLoading}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                boxSizing: 'border-box',
                background: '#fff',
                borderRadius: 14,
                padding: '16px 16px',
                marginBottom: 10,
                border: 'none',
                cursor: !isLoggedIn || isLoading ? 'not-allowed' : 'pointer',
                opacity: !isLoggedIn ? 0.5 : 1,
              }}
            >
              {/* Title — always on the left */}
              <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
                {isLoading ? '...' : plan.title}
              </span>
              {/* Price badge — always on the right */}
              <span
                style={{
                  background: '#3B7BF4',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 16,
                  padding: '8px 16px',
                  borderRadius: 10,
                  minWidth: 64,
                  textAlign: 'center',
                }}
              >
                {plan.price}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

const PaymentsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.language?.startsWith('he') ?? false;
  const direction = isRtl ? 'rtl' : 'ltr';

  const isLocalhost = window.location.hostname === 'localhost';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const effectivelyLoggedIn = isLocalhost || isLoggedIn;
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);
  const [lastPurchasedSubscription, setLastPurchasedSubscription] =
    useState<LastPurchasedSubscription | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponResult | null>(null);
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);
  const [usedCouponCodes, setUsedCouponCodes] = useState<string[]>([]);

  useEffect(() => {
    const raw = localStorage.getItem(LOGGED_IN_USER_KEY);
    if (!raw) {
      setIsLoggedIn(false);
      return;
    }
    try {
      const parsed = JSON.parse(raw) as { userId?: string; uid?: string; email?: string };
      const userId = parsed?.userId ?? parsed?.uid ?? parsed?.email ?? null;
      setIsLoggedIn(isRealLoggedInUser(userId ?? null));
      const email = parsed?.email?.trim().toLowerCase() ?? null;
      setUserEmail(email);
      if (email) {
        void getDocs(query(collection(db, 'couponUsages'), where('email', '==', email))).then(
          (snap) => {
            const codes = snap.docs.map((d) => d.data().couponCode as string).filter(Boolean);
            setUsedCouponCodes(codes.filter((c, i) => codes.indexOf(c) === i));
          },
        );
      }
    } catch {
      setIsLoggedIn(false);
    }
  }, []);

  const handleSubscribe = async (productId: string) => {
    if (!effectivelyLoggedIn) {
      setMessage({
        type: 'error',
        text: t(
          'payments_login_required',
          'You need to sign in to subscribe. Please sign in and try again.',
        ),
      });
      return;
    }
    setLoadingId(productId);
    setMessage(null);
    setShowCongratulations(false);
    setLastPurchasedSubscription(null);

    // const plan = MAIN_PLANS.find((p) => p.id === productId);
    const storeProductId =
      appliedCoupon?.discountType === 'discount' && appliedCoupon.storePlanNamePostfix
        ? productId + appliedCoupon.storePlanNamePostfix
        : productId;
    const isIosWebView =
      typeof window !== 'undefined' &&
      window.flutter_inappwebview !== undefined &&
      /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const iosProductSuffix = productId === 'month1' ? 'apple2' : 'apple';
    const finalProductId = isIosWebView ? storeProductId + iosProductSuffix : storeProductId;
    const result = await requestFlutterPurchase(finalProductId, null, finalProductId);
    setLoadingId(null);

    if (result.success) {
      trackPurchase(productId, 'store', {
        store_product_id: finalProductId,
        platform: isIosWebView ? 'ios' : 'android_web',
        coupon_code: appliedCoupon?.code,
        coupon_type: appliedCoupon?.discountType,
      });
      if (appliedCoupon?.discountType === 'percent') setAppliedCoupon(null);
      setLastPurchasedSubscription({
        productId: result.subscriptionType ?? result.productId ?? productId,
        expiresAt: result.expiresAt,
      });
      setShowCongratulations(true);
    } else {
      setMessage({
        type: 'error',
        text: result.error ?? t('payments_error', 'Something went wrong. Please try again.'),
      });
    }
  };

  const handleRedeemFreeCoupon = async (productId: string) => {
    if (!appliedCoupon || appliedCoupon.discountType !== 'free' || !userEmail) return;
    setRedeemingCoupon(true);
    setMessage(null);
    try {
      const res = await callCloudFunction('redeemCoupon', FIREBASE_FUNCTIONS.redeemCoupon(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: appliedCoupon.code, email: userEmail, productId }),
      });
      const data = (await res.json()) as { success: boolean; expiresAt?: string; error?: string };
      if (data.success) {
        trackPurchase(productId, 'coupon', {
          coupon_code: appliedCoupon.code,
          coupon_type: appliedCoupon.discountType,
        });
        setAppliedCoupon(null);
        setLastPurchasedSubscription({ productId, expiresAt: data.expiresAt });
        setShowCongratulations(true);
      } else {
        setMessage({
          type: 'error',
          text:
            data.error ?? t('coupon_redeem_error', 'Failed to redeem coupon. Please try again.'),
        });
      }
    } catch {
      setMessage({
        type: 'error',
        text: t('coupon_redeem_error', 'Failed to redeem coupon. Please try again.'),
      });
    } finally {
      setRedeemingCoupon(false);
    }
  };

  const isFreeRedeem = effectivelyLoggedIn && appliedCoupon?.discountType === 'free';

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#F9FAFB',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          maxWidth: 480,
          margin: '0 auto',
          width: '100%',
          direction,
          paddingBottom: 110,
          boxSizing: 'border-box',
        }}
      >
        {/* Hero image */}
        <div style={{ lineHeight: 0, display: 'flex', justifyContent: 'center' }}>
          <img src={cheeringKidsImg} alt="" style={{ height: 200, display: 'block' }} />
        </div>

        {/* Blue ribbon */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)',
            padding: '6px 10px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: '#fff',
              lineHeight: 1.5,
              direction: 'rtl',
            }}
          >
            הצטרפו לאלפי התלמידים שלומדים
            <br />
            עם הכלים המתקדמים ביותר
          </p>
        </div>

        <div style={{ padding: '20px 16px' }}>
          {/* Login warning */}
          {!effectivelyLoggedIn && (
            <div
              role="alert"
              style={{
                marginBottom: 20,
                padding: 16,
                borderRadius: 12,
                background: '#FEF3C7',
                border: '1px solid #F2CC0D',
                color: '#92400E',
                fontSize: 15,
                lineHeight: 1.5,
                direction,
              }}
            >
              <p style={{ margin: '0 0 12px 0' }}>
                {t(
                  'payments_login_required',
                  'You need to sign in to subscribe. Please sign in and try again.',
                )}
              </p>
              <button
                type="button"
                onClick={() => navigate('/onboarding', { state: { returnTo: '/payments' } })}
                style={{
                  padding: '10px 20px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#fff',
                  background: '#D97706',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
              >
                {t('payments_sign_in', 'Sign in')}
              </button>
            </div>
          )}

          {/* Current subscription */}
          {isLoggedIn && (
            <CurrentSubscriptionSection
              key={subscriptionRefreshKey}
              lastPurchasedSubscription={lastPurchasedSubscription}
              onSubscriptionLoaded={() => setLastPurchasedSubscription(null)}
              revalidateOnLoad
            />
          )}

          {/* Main plan cards */}
          {MAIN_PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isLoading={loadingId === plan.id}
              disabled={!effectivelyLoggedIn}
              isFreeRedeem={isFreeRedeem}
              isRedeemLoading={redeemingCoupon}
              onSubscribe={handleSubscribe}
              onRedeemFree={handleRedeemFreeCoupon}
              freeCouponDays={appliedCoupon?.discountValue}
              discountPercent={
                appliedCoupon?.discountType === 'percent' ||
                appliedCoupon?.discountType === 'discount'
                  ? appliedCoupon.discountValue
                  : undefined
              }
            />
          ))}

          {/* Coupon input */}
          {!isFreeRedeem && (
            <CouponInput
              onValidCoupon={(result) => setAppliedCoupon(result)}
              onClearCoupon={() => setAppliedCoupon(null)}
            />
          )}

          {/* Used coupons history */}
          {isLoggedIn && usedCouponCodes.length > 0 && (
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 13, color: '#6B7280' }}>
                {t('coupon_used_history', 'Used:')}
              </span>
              {usedCouponCodes.map((code) => (
                <span
                  key={code}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: 'rgba(79,70,229,0.1)',
                    color: '#4338CA',
                    letterSpacing: '0.04em',
                  }}
                >
                  {code}
                </span>
              ))}
            </div>
          )}

          {/* Error / success message */}
          {message && (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: 12,
                borderRadius: 8,
                background: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
                color: message.type === 'success' ? '#065F46' : '#991B1B',
                fontSize: 14,
              }}
            >
              {message.text}
            </div>
          )}

          {/* Free vs Premium comparison */}
          <div style={{ marginTop: 24, marginBottom: 28 }}>
            <ComparisonTable />
          </div>

          {/*Yemen special section */}
          <div>
            <YemenSection
              isLoggedIn={effectivelyLoggedIn}
              loadingId={loadingId}
              onSubscribe={handleSubscribe}
            />
          </div>
        </div>

        <SubscriptionSuccessPopup
          open={showCongratulations}
          onClose={() => {
            setShowCongratulations(false);
            setSubscriptionRefreshKey((k) => k + 1);
          }}
        />
      </div>
      <BottomNav />
    </div>
  );
};

export default PaymentsPage;
